import { useState } from 'react';
import { reportApi, blockApi } from '../api/client';

interface Props {
  mode: 'report' | 'block';
  eventId: string;
  commentId: string;
  authorName: string;
  authorUserId: string | null;
  onClose: () => void;
  onDone: () => void;
}

export function ReportOrBlockModal({ mode, eventId, commentId, authorName, authorUserId, onClose, onDone }: Props) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isReport = mode === 'report';

  async function handleSubmit() {
    if (!reason.trim()) { setError('Please tell us what happened.'); return; }
    setSubmitting(true);
    setError('');
    try {
      if (isReport) {
        await reportApi.reportComment(eventId, commentId, reason.trim());
      } else if (authorUserId) {
        await blockApi.block(authorUserId, reason.trim());
      }
      onDone();
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <p className="text-sm font-bold text-slate-900">
            {isReport ? 'Report comment' : `Block ${authorName}`}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {isReport
              ? 'This comment will be removed from view immediately and sent to us for review.'
              : `You won't see ${authorName}'s comments anymore. This is also sent to us for review.`}
          </p>
        </div>
        <div className="p-5 space-y-3">
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="What's the issue?"
            rows={3}
            className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex gap-2 justify-end">
            <button onClick={onClose} className="px-3 py-2 text-sm text-stone-500 hover:text-stone-700">Cancel</button>
            <button
              onClick={handleSubmit} disabled={submitting}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : isReport ? 'Report' : 'Block'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
